import { Alert, Button, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, TextField } from '@mui/material';
import React from 'react';

import { requestPasswordReset } from './dashboardAuth';

export const SENT_MESSAGE = "If an account exists for that email, we've sent a link to set a new password. It works for 72 hours.";

const ERROR_MESSAGES = {
    missing: 'Enter the email address you sign in with.',
    throttled: 'Too many requests. Wait a while, then try again.',
    failed: 'Something went wrong on our side. Try again in a few minutes.',
    network: 'Could not reach the server. Check your connection and try again.',
};

// "Forgot password?" on the password sign-in: asks for the email and requests a reset link.
// The answer is the same whether or not the account exists.
export default function ForgotPasswordDialog({ open, initialEmail, onClose }) {
    const [email, setEmail] = React.useState(initialEmail || '');
    const [submitting, setSubmitting] = React.useState(false);
    const [sent, setSent] = React.useState(false);
    const [error, setError] = React.useState(null);

    React.useEffect(() => {
        if (!open) { return; }
        setEmail(initialEmail || '');
        setSent(false);
        setError(null);
    }, [open, initialEmail]);

    const onSubmit = async (ev) => {
        ev.preventDefault();
        const value = email.trim();
        if (!value) {
            setError(ERROR_MESSAGES.missing);
            return;
        }
        setError(null);
        setSubmitting(true);
        try {
            const outcome = await requestPasswordReset(value);
            if (outcome === 'sent') {
                setSent(true);
            } else {
                setError(ERROR_MESSAGES[outcome]);
            }
        } catch (_e) {
            setError(ERROR_MESSAGES.network);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs" aria-labelledby="forgot-password-title">
            <form onSubmit={onSubmit} noValidate>
                <DialogTitle id="forgot-password-title">Reset your password</DialogTitle>
                <DialogContent>
                    {sent ? (
                        <Alert severity="success">{SENT_MESSAGE}</Alert>
                    ) : (
                        <>
                            <DialogContentText>
                                Enter the email address you sign in with. We'll email you a link to set a new password.
                            </DialogContentText>
                            <TextField
                                variant="outlined"
                                margin="normal"
                                fullWidth
                                autoFocus
                                id="forgot-password-email"
                                label="Email Address"
                                type="email"
                                autoComplete="email"
                                value={email}
                                onChange={(ev) => setEmail(ev.target.value)}
                                disabled={submitting}
                            />
                            {error && <Alert severity="error" sx={{ mt: 1 }}>{error}</Alert>}
                        </>
                    )}
                </DialogContent>
                <DialogActions>
                    {sent ? (
                        <Button onClick={onClose} variant="contained">Back to sign in</Button>
                    ) : (
                        <>
                            <Button onClick={onClose} disabled={submitting}>Cancel</Button>
                            <Button type="submit" variant="contained" disabled={submitting}>
                                {submitting ? 'Sending…' : 'Send link'}
                            </Button>
                        </>
                    )}
                </DialogActions>
            </form>
        </Dialog>
    );
}
